// Cantidad por modificador (repetir el mismo, ej. 2× Carne / 9× Pollo Extra).
//
// FUENTE DE VERDAD COMPARTIDA por todas las superficies de cobro (web/POS/kiosk/
// BarPos). Las reglas de dinero y de selección viven AQUÍ, no duplicadas por modal.
// Diseño: docs/superpowers/specs/2026-06-19-modifier-quantity-design.md
//
// Reglas:
// - D5 — La cantidad (repetir el mismo modificador) se habilita automáticamente
//   cuando la opción tiene costo extra (price_delta > 0) O el grupo es de conteo
//   requerido (te obliga a elegir N, ej. triple = 3 proteínas). Los modificadores
//   gratis de remoción (Sin Tomate, NO Ketchup) quedan como toggle, sin cantidad.
// - D9 — Tope: extras pagos hasta su max_selections, topado en MAX_MODIFIER_QTY
//   (9); conteo requerido acotado por su max_selections (ej. 3). El máximo y el
//   mínimo se validan por SUMA de cantidades, no por opciones distintas.
//   OJO: el servidor (lib/menu/validateModifierSelections.ts) rechaza con 422
//   todo lo que pase max_selections en las 6 rutas de escritura — el tope de la
//   UI nunca puede ser más laxo que el configurado (2026-07-31: el 9 plano de
//   extras pagos ignoraba el max del grupo y el mesero recibía el 422 en caja).

import { includedSelectionsRuleOf } from "@/lib/menu/includedSelections"

export const MAX_MODIFIER_QTY = 9

export type ModGroupRule = {
  required: boolean
  /** 0 si no aplica. */
  minSelections: number
  /** 1 = single-select. */
  maxSelections: number
  /** Grupo de «selecciones incluidas» cuyas incluidas van juntas en UNA porción (el
   *  mixto). Ausente = no. Ver lib/menu/includedSelections.ts. */
  sharedPortion?: true
}

export type ModOptionRule = {
  priceDelta: number
}

/** Mínimo efectivo de selección del grupo (resuelve `required` → 1). */
export function effectiveMin(g: ModGroupRule): number {
  if (g.minSelections > 0) return g.minSelections
  return g.required ? 1 : 0
}

/** ¿El grupo obliga a elegir una cantidad (>1)? Ej: triple = 3 proteínas. */
export function groupRequiresCount(g: ModGroupRule): boolean {
  return g.maxSelections > 1 && effectiveMin(g) >= 1
}

/** ¿Esta opción admite cantidad (stepper)? Ver D5. En un grupo de porción compartida
 *  (el mixto) nunca: «mixto de pernil con pernil» no significa nada — cada opción es un
 *  toggle y el cupo cuenta proteínas distintas. */
export function canRepeat(g: ModGroupRule, o: ModOptionRule): boolean {
  if (g.sharedPortion) return false
  return g.maxSelections > 1 && ((o.priceDelta ?? 0) > 0 || groupRequiresCount(g))
}

/** ¿El grupo tiene al menos una opción repetible paga? */
function hasPaidRepeatable(g: ModGroupRule, options: ModOptionRule[]): boolean {
  return g.maxSelections > 1 && options.some((o) => (o.priceDelta ?? 0) > 0)
}

/**
 * Tope TOTAL de unidades del grupo (suma de cantidades). Ver D9.
 * - conteo requerido (triple) → `max_selections` (ej. 3)
 * - extras pagos → `max_selections`, topado en `MAX_MODIFIER_QTY` (9)
 * - resto (no repetible / remoción) → `max_selections`
 */
export function groupMaxUnits(g: ModGroupRule, options: ModOptionRule[]): number {
  if (groupRequiresCount(g)) return Math.max(1, g.maxSelections)
  if (hasPaidRepeatable(g, options)) return Math.min(MAX_MODIFIER_QTY, g.maxSelections)
  return Math.max(1, g.maxSelections)
}

/**
 * Tope por opción individual. La UI además debe respetar la capacidad restante
 * del grupo (groupMaxUnits − suma de las demás).
 */
export function maxQtyForOption(
  g: ModGroupRule,
  o: ModOptionRule,
  options: ModOptionRule[]
): number {
  if (!canRepeat(g, o)) return 1
  return Math.min(MAX_MODIFIER_QTY, groupMaxUnits(g, options))
}

/** ¿La selección del grupo cumple min/max por SUMA de cantidades? Ver D4. */
export function groupSelectionValid(
  g: ModGroupRule,
  options: ModOptionRule[],
  totalUnits: number
): boolean {
  return totalUnits >= effectiveMin(g) && totalUnits <= groupMaxUnits(g, options)
}

/**
 * Subtotal de modificadores = Σ (price_delta × quantity). `eff` mapea el delta al
 * precio efectivo (cash/card) por unidad; por defecto identidad. Redondea a centavos.
 */
export function modifierUnitsSubtotal(
  selected: Array<{ priceDelta: number; quantity: number }>,
  eff: (delta: number) => number = (d) => d
): number {
  const raw = selected.reduce(
    (s, m) => s + eff(m.priceDelta) * Math.max(0, m.quantity),
    0
  )
  return Math.round(raw * 100) / 100
}

/**
 * Etiqueta de un modificador para mostrar (carrito/KDS/comanda/recibo): antepone
 * `N×` cuando la cantidad es mayor a 1. Formatear AQUÍ (capa de datos) evita
 * tocar cada renderer de impresora/pantalla.
 */
export function formatModifierLabel(optionName: string, quantity?: number | null): string {
  const q = quantity ?? 1
  return q > 1 ? `${q}× ${optionName}` : optionName
}

/**
 * Normaliza el shape suelto de la DB a `ModGroupRule`. Web usa `required`, POS usa
 * `is_required`. Nulls → defaults seguros (single-select, no requerido).
 */
export function normalizeGroupRule(raw: {
  required?: boolean | null
  is_required?: boolean | null
  min_selections?: number | null
  max_selections?: number | null
  included_selections?: number | null
  included_shared_portion?: boolean | null
  zonable?: boolean | null
}): ModGroupRule {
  return {
    required: (raw.required ?? raw.is_required) === true,
    minSelections: raw.min_selections ?? 0,
    maxSelections: raw.max_selections ?? 1,
    // Solo cuando aplica: la forma de siempre no cambia para ningún otro grupo.
    ...(includedSelectionsRuleOf(raw)?.sharedPortion ? { sharedPortion: true as const } : {}),
  }
}
